import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { useSelector } from 'react-redux';
import IconNavigation from '@shared/icon-navigation';
import SideMenu from '@shared/external-cs-auth.side-menu';
import AccountNavigation from '@shared/account-navigation';
import HamburgerNavigation from '@shared/hamburger-navigation';
import connect from '@clearscore/redux.connect';
import { selectors as profileSelectors } from '@clearscore/redux.profile';
import { selectors as notificationSelectors } from '@clearscore/redux.notifications';
import { NavLink } from 'react-router-dom';
import Media from 'react-media';
import useMedia from '@clearscore/hooks.use-media';
import Container from '@clearscore/rainbow.container';
import Logo from '@clearscore/rainbow.icons.clearscore-logo-wordmark';
import ClearScoreSymbol from '@clearscore/rainbow.icons.clearscore-logo-symbol';
import NotificationIcon from '@clearscore/rainbow.icons.notification-hoverable';
import { mediaQuery } from '@clearscore/rainbow.tokens';
import useLanguage from '@clearscore/hooks.use-language';
import { useHasSegmentationGroups } from '@clearscore/hooks.use-segmentation';
import Spacer from '@clearscore/rainbow.spacer';
import cx from 'classnames';
import ExclusiveModal from '@shared/exclusive-modal';

import lang from './lib/lang';
import Notifications from '../notifications';
import NotificationsDrawer from '../notifications-drawer';
import styles from './header.css';

const Header = ({ routerTrackingSource, hasDefectedReport, isSimpleHeader }) => {
    const language = useLanguage(lang);
    const { firstName } = useSelector(profileSelectors.getSimple);
    const hasNewNotifications = useSelector(notificationSelectors.getHasNewNotifications);
    const [isBurgerMenuOpen, toggleBurgerMenu] = useState(false);
    const [isAccountMenuOpen, toggleAccountMenu] = useState(false);
    const [drawer, setDrawer] = useState({});
    const [isModalShown, setIsModalShown] = useState(false);
    const hamburgerNotifications = 0; // todo: hook up routes selector
    const accountNotifications = 0; // todo: hook up routes selector
    const notificationsClass = cx(styles.notificationsWrapper, {
        [styles.notification]: hasNewNotifications,
    });
    const matches = useMedia([null, mediaQuery['rnb-mq-huge']], [true, false]);
    const logo = matches ? <ClearScoreSymbol width={20} height={20} /> : undefined;

    const headerClass = cx(styles.header, {
        [styles.simpleHeader]: isSimpleHeader,
    });

    const notificationsEnabled = useHasSegmentationGroups('test-in-app-notifications-v1:test');

    const handleSetDrawer = (content) => () => {
        setDrawer({ content });
    };

    const handleDrawerClose = () => {
        setDrawer({});
    };

    const handleModalVisibility = () => {
        setIsModalShown(!isModalShown);
    };

    const centerLogo = (
        <NavLink to="/" className={styles.headerCenterLogo} data-qa="head-center-logo">
            <div className={styles.logoWrapper}>
                <Logo />
            </div>
        </NavLink>
    );

    return (
        <header role="navigation" id="webapp-core-header" data-walkthrough="webapp-core-header">
            {/* id="webapp-core-header" is required for dynamic dashboard */}
            <Container
                size={Container.sizes.HUGE}
                theme={isSimpleHeader ? Container.themes.DARK : Container.themes.TRANSPARENT}
            >
                <div className={headerClass}>
                    <HamburgerNavigation
                        hasBurgerMenu={false}
                        handleToggle={() => toggleBurgerMenu(!isBurgerMenuOpen)}
                        noNotifications={isBurgerMenuOpen ? undefined : hamburgerNotifications}
                        logo={logo}
                    />
                    <Media query={mediaQuery['rnb-mq-large']}>
                        {(mediaMatches) => (
                            <Choose>
                                <When condition={mediaMatches && !hasDefectedReport && !isSimpleHeader}>
                                    <div data-unmasked>
                                        <IconNavigation
                                            className={styles.headerCenter}
                                            menu="primary"
                                            routerTrackingSource={routerTrackingSource}
                                        />
                                    </div>
                                </When>

                                <Otherwise>
                                    {notificationsEnabled ? (
                                        <Spacer all={{ left: Spacer.spacings.SMALL }}>{centerLogo}</Spacer>
                                    ) : (
                                        centerLogo
                                    )}
                                </Otherwise>
                            </Choose>
                        )}
                    </Media>
                    <div className={styles.topRightIconWrapper}>
                        <AccountNavigation
                            firstName={isSimpleHeader ? null : firstName}
                            routerTrackingSource={routerTrackingSource}
                            handleAccountToggle={() => toggleAccountMenu(!isAccountMenuOpen)}
                            noNotifications={isAccountMenuOpen ? undefined : accountNotifications}
                        />
                        {notificationsEnabled && !isSimpleHeader && (
                            <div className={notificationsClass} data-id="notifications-section">
                                <button
                                    type="button"
                                    className={styles.notificationsContainer}
                                    data-id="notification-icon"
                                    onClick={handleSetDrawer(
                                        <Notifications
                                            handleDrawerClose={handleDrawerClose}
                                            hasNewNotifications={hasNewNotifications}
                                            handleModalVisibility={handleModalVisibility}
                                        />,
                                    )}
                                >
                                    <If condition={hasNewNotifications}>
                                        <span aria-label={language.newNotificationAria} className={styles.badge} />
                                    </If>
                                    <NotificationIcon height={32} width={32} />
                                </button>
                            </div>
                        )}
                    </div>
                    <NotificationsDrawer onClose={handleDrawerClose}>{drawer.content}</NotificationsDrawer>
                    <ExclusiveModal handleModalVisibility={handleModalVisibility} isModalShown={isModalShown} />
                    <SideMenu
                        drawerDataId="header-side-menu"
                        isOpen={isAccountMenuOpen}
                        accountNotifications={accountNotifications}
                        closeDrawer={() => toggleAccountMenu(!isAccountMenuOpen)}
                    />
                </div>
            </Container>
        </header>
    );
};

Header.propTypes = {
    routerTrackingSource: PropTypes.string,
    hasDefectedReport: PropTypes.bool,
    isSimpleHeader: PropTypes.bool,
};

Header.defaultProps = {
    routerTrackingSource: '',
    hasDefectedReport: false,
    isSimpleHeader: false,
};

export default connect(null, null, { useRouter: true })(Header);
